import { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './styles.css';
import {
    DeleteFilled,
    HeartFilled,
    HeartOutlined,
    MinusOutlined,
    PlusOutlined,
    EditOutlined,
    GiftFilled,
    ShoppingCartOutlined,
    ReloadOutlined,
    CloseCircleOutlined,
    LoadingOutlined
} from '@ant-design/icons';
import {
    Button,
    Input,
    Checkbox,
    ConfigProvider,
    Modal,
    Spin
} from 'antd';
import {useDispatch, useSelector} from "react-redux";
import { multiplyRemoveItem, cartActions, changeCartItemQuantity, cartSelectors } from "../../features/cart";
import { toggleFavorite } from "../../features/favorites";
import { selectIsItemFavorite } from "../../features/favorites";
import {
    shippingSelectors,
    shippingActions,
    calculateShipping,
    SHIPPING_TYPES,
    SHIPPING_METHODS
} from "../../features/shipping";
import {
    promoSelectors,
    applyPromoCode,
    clearPromoCode
} from "../../features/promo";
import {
    bonusesSelectors,
    fetchBonuses,
    applyBonusPoints,
    clearBonusPoints,
    calcBonusPoints
} from "../../features/bonuses";
import AddressFormCart from '../../features/address/AddressFormCart';
import AddressInput from './AddressInput';
import ShippingOptions from './ShippingOptions';
import ConfirmOrderButton from "./ConfirmOrderButton";
import { ADDRESS_FIELD_NAMES, addressSelectors, addressActions } from '../../features/address';
import { fetchUserData } from '../../features/auth';
import VariationModal, { hasVariations } from "../../components/VariationModal";

const VARIATION_LABELS = {
    'pa_package-type': 'Упаковка',
    'pa_warehouse': 'Склад отгрузки',
    'pa_sizeof': 'Размер',
    'pa_color': 'Цвет',
    'pa_vet': 'Специалист',
    'pa_pcr': 'Тип исследования',
};

const variationLabel = (slug, item) => {
    if (VARIATION_LABELS[slug]) return VARIATION_LABELS[slug];
    const attr = item?.attributes?.find(a => a.slug === slug);
    if (attr?.name) return attr.name;
    return slug;
};

const FavoriteButton = ({ item }) => {
    const dispatch = useDispatch();
    const isFavorite = useSelector((state) => selectIsItemFavorite(state, item.id));

    const handleToggleFavorite = (e) => {
        e.stopPropagation();
        dispatch(toggleFavorite(item));
    };

    return isFavorite ? (
        <HeartFilled
            className="action-icon favorite-active"
            onClick={handleToggleFavorite}
        />
    ) : (
        <HeartOutlined
            className="action-icon"
            onClick={handleToggleFavorite}
        />
    );
};

const Cart = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const {
        selectCartItems,
        selectCartTotalQuantity,
    } = cartSelectors;

    const items = useSelector(state => selectCartItems(state));
    const cartCount = useSelector(selectCartTotalQuantity);
    const addressFields = useSelector(addressSelectors.selectAddressFields);

    const shippingAddress = useSelector(shippingSelectors.selectAddress);
    const shippingCost = useSelector(shippingSelectors.selectSelectedShippingCost);
    const deliveryDays = useSelector(shippingSelectors.selectSelectedDeliveryDays);
    const isShippingLoading = useSelector(shippingSelectors.selectIsLoading);
    const hasShippingOptions = useSelector(shippingSelectors.selectHasShippingOptions);
    const allVirtual = useSelector(cartSelectors.selectAllItemsVirtual);
    const selectedShippingData = useSelector(shippingSelectors.selectSelectedShippingData);
    const isOwnPickup = selectedShippingData?.shipping_method === SHIPPING_METHODS.OWN
        && selectedShippingData?.shipping_type === SHIPPING_TYPES.LOCAL_PICKUP;

    const [selectedItems, setSelectedItems] = useState([]);
    const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
    const [promoCode, setPromoCode] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('tinkoff');
    const [isAgreed, setIsAgreed] = useState(true);

    const [editingItem, setEditingItem] = useState(null);
    const [isTermsOpen, setIsTermsOpen] = useState(false);

    useEffect(() => {
        if (paymentMethod === 'cod' && !isOwnPickup) {
            setPaymentMethod('tinkoff');
        }
    }, [paymentMethod, isOwnPickup]);

    const authUser = useSelector(state => state.auth?.user);
    const userData = useMemo(() => {
        if (!authUser) return null;
        const billing = authUser.billing || {};
        return {
            first_name: authUser.first_name || billing.first_name || '',
            last_name: authUser.last_name || billing.last_name || '',
            phone_country: authUser.phone_country || billing.phone_country || 'RU',
            phone: authUser.phone || billing.phone || '',
            email: authUser.email || billing.email || '',
            is_juridic: authUser.is_juridic ?? billing.is_juridic,
            company: authUser.company || billing.company || '',
            company_inn: authUser.company_inn || billing.company_inn || '',
            company_kpp: authUser.company_kpp || billing.company_kpp || '',
            company_ogrn: authUser.company_ogrn || billing.company_ogrn || '',
            company_chief_name: authUser.company_chief_name || billing.company_chief_name || '',
            company_contact_name: authUser.company_contact_name || billing.company_contact_name || '',
            company_job_title: authUser.company_job_title || billing.company_job_title || '',
            company_scope: authUser.company_scope || billing.company_scope || '',
            company_legal_address: authUser.company_legal_address || billing.company_legal_address || '',
            company_post_address: authUser.company_post_address || billing.company_post_address || '',
        };
    }, [authUser]);

    const promoDiscountAmount = useSelector(promoSelectors.selectDiscount);
    const promoCoupon = useSelector(promoSelectors.selectCoupon);
    const isPromoApplied = useSelector(promoSelectors.selectIsApplied);
    const isPromoLoading = useSelector(promoSelectors.selectIsLoading);

    const availablePoints = useSelector(bonusesSelectors.selectAvailableToSpend);
    const isBonusApplied = useSelector(bonusesSelectors.selectIsBonusApplied);
    const bonusDiscountAmount = useSelector(bonusesSelectors.selectBonusDiscount);
    const isBonusApplying = useSelector(bonusesSelectors.selectIsBonusesApplying);
    const bonusAppliedCartTotal = useSelector(bonusesSelectors.selectBonusAppliedCartTotal);

    const shippingRecalcTimer = useRef(null);
    const isLoggedIn = useSelector(state => state.auth?.isLoggedIn);

    const profileFields = useSelector(state => state.profile?.fields);

    useEffect(() => {
        if (isLoggedIn) {
            dispatch(fetchUserData());
        }
    }, []);

    useEffect(() => {
        if (isLoggedIn) {
            dispatch(fetchBonuses());
        }
    }, [isLoggedIn]);

    useEffect(() => {
        const profileHasData = profileFields?.firstName || profileFields?.lastName || profileFields?.email;
        const source = profileHasData ? profileFields : userData;
        if (!source) return;

        const fieldMap = profileHasData ? {
            [ADDRESS_FIELD_NAMES.FIRSTNAME]: source.firstName,
            [ADDRESS_FIELD_NAMES.LASTNAME]: source.lastName,
            [ADDRESS_FIELD_NAMES.PHONE_COUNTRY]: source.countryPhoneCode || source.phoneCountry,
            [ADDRESS_FIELD_NAMES.PHONE]: source.phone,
            [ADDRESS_FIELD_NAMES.EMAIL]: source.email,
        } : {
            [ADDRESS_FIELD_NAMES.FIRSTNAME]: source.first_name,
            [ADDRESS_FIELD_NAMES.LASTNAME]: source.last_name,
            [ADDRESS_FIELD_NAMES.PHONE_COUNTRY]: source.phone_country,
            [ADDRESS_FIELD_NAMES.PHONE]: source.phone,
            [ADDRESS_FIELD_NAMES.EMAIL]: source.email,
        };

        const updates = {};
        let hasChanges = false;
        Object.entries(fieldMap).forEach(([key, val]) => {
            if (!addressFields[key] && val) {
                updates[key] = val;
                hasChanges = true;
            }
        });

        if (hasChanges) {
            dispatch(addressActions.setAddressFields(updates));
        }
    }, [profileFields, userData]);

    useEffect(() => {
        if (shippingAddress?.fullAddress) return;

        if (authUser) {
            const shipping = authUser.shipping || {};
            const billing = authUser.billing || {};
            const src = shipping.address_1 ? shipping : billing;

            if (src.address_1 || src.city) {
                const parts = [src.address_1, src.address_2, src.city, src.state, src.postcode, src.country].filter(Boolean);
                const fullAddress = parts.join(', ');

                dispatch(shippingActions.setAddress({
                    fullAddress,
                    city: src.city || '',
                    postalCode: src.postcode || '',
                    region: src.state || '',
                    stateCode: src.state || '',
                }));
                return;
            }
        }

        if (profileFields) {
            const city = profileFields.city;
            const postalCode = profileFields.postalCode;
            const regionCode = profileFields.region;

            if (!city && !regionCode) return;

            const parts = [city, postalCode].filter(Boolean);
            const fullAddress = parts.join(', ');

            dispatch(shippingActions.setAddress({
                fullAddress,
                city: city || '',
                postalCode: postalCode || '',
                region: regionCode || '',
                stateCode: regionCode || '',
            }));
        }
    }, [authUser, profileFields]);

    const selectedCartItems = useMemo(() => {
        return items.filter(item =>
            selectedItems.some(selected => selected.cartItemId === item.cartItemId)
        );
    }, [items, selectedItems]);

    const selectedItemsKey = useMemo(() => {
        return selectedItems
            .map(item => `${item.cartItemId}:${item.quantity}`)
            .sort()
            .join(',');
    }, [selectedItems]);

    useEffect(() => {
        if (items.length > 0) {
            const allSelected = items.map(item => ({
                cartItemId: item.cartItemId,
                id: item.id,
                quantity: item.quantity,
                price: item.price
            }));
            setSelectedItems(allSelected);
        } else {
            setSelectedItems([]);
        }
    }, [items.length]);

    useEffect(() => {
        setSelectedItems(prev =>
            prev.map(selected => {
                const cartItem = items.find(item => item.cartItemId === selected.cartItemId);
                if (cartItem && cartItem.quantity !== selected.quantity) {
                    return { ...selected, quantity: cartItem.quantity };
                }
                return selected;
            })
        );
    }, [items]);

    useEffect(() => {
        if (shippingRecalcTimer.current) {
            clearTimeout(shippingRecalcTimer.current);
        }

        if (selectedCartItems.length === 0) {
            return;
        }

        if (shippingAddress?.fullAddress && selectedCartItems.length > 0) {
            shippingRecalcTimer.current = setTimeout(() => {
                const itemsForShipping = selectedCartItems.map(item => {
                    const selectedItem = selectedItems.find(s => s.cartItemId === item.cartItemId);
                    return {
                        ...item,
                        quantity: selectedItem?.quantity || item.quantity
                    };
                });

                dispatch(calculateShipping(itemsForShipping, {
                    country: 'RU',
                    fullAddress: shippingAddress.fullAddress || '',
                    city: shippingAddress.city || '',
                    stateCode: shippingAddress.stateCode || '',
                    geoLat: shippingAddress.geoLat || '',
                    geoLon: shippingAddress.geoLon || '',
                    postal_code: shippingAddress.postalCode || '',
                }));
            }, 800);
        }

        return () => {
            if (shippingRecalcTimer.current) {
                clearTimeout(shippingRecalcTimer.current);
            }
        };
    }, [selectedItemsKey, shippingAddress?.fullAddress]);

    const handleNavigate = (id) => {
        navigate(`/product/${id}`);
    };

    const handleDelete = (e, cartItemId) => {
        e.stopPropagation();
        dispatch(cartActions.removeItem(cartItemId));
    }

    const handleChangeQuantity = (e, cartItemId, newQuantity) => {
        e.stopPropagation();
        dispatch(changeCartItemQuantity(cartItemId, newQuantity));

        setSelectedItems(prev =>
            prev.map(item =>
                item.cartItemId === cartItemId ? { ...item, quantity: newQuantity } : item
            )
        );
    };

    const handleCheckboxChange = (e, item) => {
        e.stopPropagation();
        const isChecked = e.target.checked;

        if (isChecked) {
            setSelectedItems(prev => [...prev, {
                cartItemId: item.cartItemId,
                id: item.id,
                quantity: item.quantity,
                price: item.price
            }]);
        } else {
            setSelectedItems(prev => prev.filter(selected => selected.cartItemId !== item.cartItemId));
        }
    };

    const isItemSelected = (cartItemId) => {
        return selectedItems.some(item => item.cartItemId === cartItemId);
    };

    const handleClearCart = () => {
        dispatch(cartActions.clearCart());
        setSelectedItems([]);
    };

    const itemsPrice = selectedItems.reduce((acc, item) => {
        return acc + item.quantity * (item.price || 0);
    }, 0);

    const promoDiscount = isPromoApplied ? Math.min(itemsPrice, promoDiscountAmount) : 0;
    const bonusDiscount = isBonusApplied ? Math.min(itemsPrice, bonusDiscountAmount) : 0;
    const actualDiscount = bonusDiscount || promoDiscount;
    const isDiscountApplied = isPromoApplied || isBonusApplied;
    const discountedItemsPrice = Math.max(0, itemsPrice - actualDiscount);
    const totalPrice = Math.max(0, discountedItemsPrice + shippingCost);

    const vatAmount = Math.round(totalPrice * 0.2);

    const handleRemoveSelected = () => {
        const cartItemIds = selectedItems.map(item => item.cartItemId);
        dispatch(multiplyRemoveItem(cartItemIds));
        setSelectedItems([]);
    };

    const handleOpenAddressModal = () => {
        setIsAddressModalOpen(true);
    };

    const handleCloseAddressModal = () => {
        setIsAddressModalOpen(false);
    };

    const handleEditVariation = (e, item) => {
        e.stopPropagation();
        setEditingItem(item);
    };

    const handleApplyPromo = () => {
        if (!promoCode.trim()) return;

        const cartForPromo = selectedItems.map(item => ({
            id: item.id,
            price: item.price,
            quantity: item.quantity,
        }));

        dispatch(applyPromoCode(promoCode.trim(), cartForPromo));
    };

    const handleClearPromo = () => {
        setPromoCode('');
        dispatch(clearPromoCode());
    };

    const handleApplyBonuses = () => {
        if (itemsPrice <= 0) return;
        dispatch(applyBonusPoints(itemsPrice));
    };

    const handleClearBonuses = () => {
        dispatch(clearBonusPoints());
    };

    useEffect(() => {
        if (isBonusApplied && itemsPrice !== bonusAppliedCartTotal) {
            dispatch(clearBonusPoints(true));
        }
    }, [itemsPrice, isBonusApplied, bonusAppliedCartTotal]);

    const handleRecalculateShipping = () => {
        if (shippingAddress?.fullAddress && selectedCartItems.length > 0) {
            const itemsForShipping = selectedCartItems.map(item => {
                const selectedItem = selectedItems.find(s => s.cartItemId === item.cartItemId);
                return {
                    ...item,
                    quantity: selectedItem?.quantity || item.quantity
                };
            });

            dispatch(calculateShipping(itemsForShipping, {
                country: 'RU',
                fullAddress: shippingAddress.fullAddress || '',
                city: shippingAddress.city || '',
                stateCode: shippingAddress.stateCode || '',
                geoLat: shippingAddress.geoLat || '',
                geoLon: shippingAddress.geoLon || '',
                postal_code: shippingAddress.postalCode || '',
            }));
        }
    };

    const isRecipientFilled = addressFields[ADDRESS_FIELD_NAMES.FIRSTNAME] &&
        addressFields[ADDRESS_FIELD_NAMES.LASTNAME] &&
        addressFields[ADDRESS_FIELD_NAMES.PHONE];

    const getRecipientDisplay = () => {
        if (!isRecipientFilled) {
            return <div className="block-content error-text">Не указан</div>;
        }

        const isJuridic = addressFields[ADDRESS_FIELD_NAMES.IS_JURIDIC];

        return (
            <div className="block-content filled-content">
                <div><strong>{addressFields[ADDRESS_FIELD_NAMES.FIRSTNAME]} {addressFields[ADDRESS_FIELD_NAMES.LASTNAME]}</strong></div>
                <div>{addressFields[ADDRESS_FIELD_NAMES.PHONE]}</div>
                {addressFields[ADDRESS_FIELD_NAMES.EMAIL] && <div>{addressFields[ADDRESS_FIELD_NAMES.EMAIL]}</div>}
                {isJuridic && addressFields[ADDRESS_FIELD_NAMES.COMPANY] && (
                    <>
                        <div className="company-info-divider"></div>
                        <div><strong>{addressFields[ADDRESS_FIELD_NAMES.COMPANY]}</strong></div>
                        {addressFields[ADDRESS_FIELD_NAMES.COMPANY_INN] && <div>ИНН: {addressFields[ADDRESS_FIELD_NAMES.COMPANY_INN]}</div>}
                        {addressFields[ADDRESS_FIELD_NAMES.COMPANY_JOB_TITLE] && <div>{addressFields[ADDRESS_FIELD_NAMES.COMPANY_JOB_TITLE]}</div>}
                    </>
                )}
            </div>
        );
    };

    const getAddressDisplay = () => {
        if (!shippingAddress?.fullAddress) {
            return null;
        }

        return (
            <div className="block-content filled-content">
                <div>{shippingAddress.fullAddress}</div>
                {shippingAddress.postalCode && (
                    <div style={{ marginTop: 4, color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
                        Индекс: {shippingAddress.postalCode}
                    </div>
                )}
            </div>
        );
    };

    return (
        <ConfigProvider
            theme={{
                token: {
                    colorPrimary: '#D00202',
                    colorText: '#ffffff',
                    borderRadius: 4,
                },
                components: {
                    Checkbox: {
                        colorPrimary: '#D00202',
                        colorPrimaryHover: '#ff3333',
                        colorBgContainer: 'transparent',
                        colorBorder: '#D00202',
                    },
                    Input: {
                        colorBgContainer: 'transparent',
                        colorBorder: '#555',
                        colorText: '#fff',
                        colorTextPlaceholder: '#888',
                    },
                    Button: {
                        colorPrimaryHover: '#ff3333',
                    },
                    Radio: {
                        colorPrimary: '#D00202',
                    }
                }
            }}
        >
            <div className="checkout-page-wrapper">
                <div className="checkout-top-container">
                    <div className="checkout-header-banner">
                        Корзина
                        {cartCount > 0 && <span className="checkout-header-count">{cartCount}</span>}
                    </div>
                </div>

                <div className="checkout-content">
                    <div className="cart-items-list">
                        {items.length === 0 ? (
                            <div className="empty-cart">
                                <ShoppingCartOutlined className="empty-cart-icon" />
                                <h2 className="empty-cart-title">Корзина пуста</h2>
                                <p className="empty-cart-text">
                                    Добавьте товары в корзину, чтобы оформить заказ
                                </p>
                                <Button
                                    type="primary"
                                    className="empty-cart-btn"
                                    onClick={() => navigate('/')}
                                >
                                    Перейти к покупкам
                                </Button>
                            </div>
                        ) : (
                            <>
                                {items.map(item => (
                                    <div
                                        key={item.cartItemId}
                                        className="cart-item-row"
                                        onClick={() => handleNavigate(item.id)}
                                    >
                                        <Checkbox
                                            className="cart-item-checkbox big-checkbox"
                                            checked={isItemSelected(item.cartItemId)}
                                            onChange={(e) => handleCheckboxChange(e, item)}
                                            onClick={(e) => e.stopPropagation()}
                                        />

                                        <div className="cart-item-image">
                                            {(item.images?.[0]?.src || item.main_image) ? (
                                                <img src={item.images?.[0]?.src || item.main_image} alt={item.name || 'Товар'} loading="lazy" />
                                            ) : (
                                                <div className="cart-item-image-placeholder">Нет фото</div>
                                            )}
                                        </div>

                                        <div className="cart-item-info">
                                            <h3 className="item-title">{item.name || 'Без названия'}</h3>
                                            {item.packaging && (
                                                <div className="item-detail">
                                                    Упаковка: <span className="item-detail-val">{item.packaging}</span>
                                                </div>
                                            )}
                                            {item.warehouse && (
                                                <div className="item-detail">
                                                    Склад отгрузки: <span className="item-detail-val">{item.warehouse}</span>
                                                </div>
                                            )}
                                            {item.selectedVariations && Object.keys(item.selectedVariations).length > 0 && (
                                                Object.entries(item.selectedVariations)
                                                    .filter(([key]) => key !== 'packaging' && key !== 'city' && key !== 'pa_package-type' && key !== 'pa_warehouse')
                                                    .map(([key, value]) => (
                                                        <div className="item-detail" key={key}>
                                                            {variationLabel(key, item)}: <span className="item-detail-val">{decodeURIComponent(value)}</span>
                                                        </div>
                                                    ))
                                            )}
                                        </div>

                                        <div className="cart-item-controls">
                                            <div className="quantity-stepper">
                                                <button
                                                    className="qty-btn"
                                                    onClick={(e) => handleChangeQuantity(e, item.cartItemId, item.quantity - 1)}
                                                    disabled={item.quantity <= 1}>
                                                    <MinusOutlined />
                                                </button>
                                                <div className="qty-value">{item.quantity}</div>
                                                <button
                                                    className="qty-btn"
                                                    onClick={(e) => handleChangeQuantity(e, item.cartItemId, item.quantity + 1)}
                                                >
                                                    <PlusOutlined />
                                                </button>
                                            </div>
                                            <div className="cart-item-price-block">
                                                <div className="item-price">
                                                    {item.price ? `${Math.round(Number(item.price)).toLocaleString()}₽` : 'Цена не указана'}
                                                </div>
                                                {calcBonusPoints(item.price * item.quantity) > 0 && (
                                                    <div className="item-bonuses">
                                                        <GiftFilled className="bonus-icon" /> +{calcBonusPoints(item.price * item.quantity).toLocaleString('ru-RU')} бонусов
                                                    </div>
                                                )}
                                            </div>
                                            <div className="item-actions">
                                                {hasVariations(item) && (
                                                    <EditOutlined
                                                        className="action-icon"
                                                        onClick={(e) => handleEditVariation(e, item)}
                                                        title="Изменить параметры"
                                                    />
                                                )}
                                                <FavoriteButton item={item} />
                                                <DeleteFilled onClick={(e) => handleDelete(e, item.cartItemId)} className="action-icon" />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}
                    </div>

                    {items.length > 0 && (
                        <div className="order-summary-sidebar">
                            <div className="order-summary-card">
                                <h2 className="summary-section-title">Детали заказа</h2>

                                <div className="summary-row">
                                    <span>Товары ({selectedItems.length})</span>
                                    {isDiscountApplied && actualDiscount > 0 ? (
                                        <span className="summary-price-discounted">
                                            <span className="price-old">{Math.round(itemsPrice).toLocaleString()}₽</span>
                                            <span className="price-new">{Math.round(discountedItemsPrice).toLocaleString()}₽</span>
                                        </span>
                                    ) : (
                                        <span>{Math.round(itemsPrice).toLocaleString()}₽</span>
                                    )}
                                </div>

                                {!allVirtual && shippingCost > 0 && (
                                    <div className="summary-row">
                                        <span>Доставка {deliveryDays ? `(${deliveryDays} дн.)` : ''}</span>
                                        <span>{Math.round(shippingCost).toLocaleString()}₽</span>
                                    </div>
                                )}

                                {!allVirtual && shippingCost === 0 && hasShippingOptions && (
                                    <div className="summary-row" style={{ color: 'var(--color-success)' }}>
                                        <span>Доставка</span>
                                        <span>бесплатно</span>
                                    </div>
                                )}

                                {isDiscountApplied && actualDiscount > 0 && (
                                    <div className="summary-row discount">
                                        <span>{isBonusApplied ? 'Оплата баллами' : `Скидка (${promoCoupon})`}</span>
                                        <span>-{Math.round(actualDiscount).toLocaleString()}₽</span>
                                    </div>
                                )}

                                {isLoggedIn && (availablePoints > 0 || isBonusApplied) && (
                                    <div className="bonus-block">
                                        {isBonusApplied ? (
                                            <div className="promo-applied">
                                                <span className="promo-applied-text">
                                                    <GiftFilled className="bonus-icon" /> Списано <b>{Math.round(bonusDiscountAmount).toLocaleString()}</b> баллов
                                                </span>
                                                <CloseCircleOutlined
                                                    className="promo-clear-icon"
                                                    onClick={handleClearBonuses}
                                                />
                                            </div>
                                        ) : (
                                            <>
                                                <span className="bonus-available">
                                                    <GiftFilled className="bonus-icon" /> У вас {Math.round(availablePoints).toLocaleString()} баллов
                                                </span>
                                                <Button
                                                    type="primary"
                                                    className="promo-btn"
                                                    onClick={handleApplyBonuses}
                                                    disabled={isBonusApplying || isPromoApplied || itemsPrice <= 0}
                                                    title={isPromoApplied ? 'Баллы нельзя использовать вместе с промокодом' : ''}
                                                >
                                                    {isBonusApplying ? <Spin indicator={<LoadingOutlined spin />} size="small" /> : 'Списать'}
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                )}

                                <div className="promo-block">
                                    {isPromoApplied ? (
                                        <div className="promo-applied">
                                            <span className="promo-applied-text">
                                                Промокод <b>{promoCoupon}</b> применён
                                            </span>
                                            <CloseCircleOutlined
                                                className="promo-clear-icon"
                                                onClick={handleClearPromo}
                                            />
                                        </div>
                                    ) : (
                                        <>
                                            <Input
                                                placeholder={isBonusApplied ? 'Недоступно при оплате баллами' : 'Промокод'}
                                                className="promo-input"
                                                value={promoCode}
                                                onChange={(e) => setPromoCode(e.target.value)}
                                                onPressEnter={handleApplyPromo}
                                                disabled={isPromoLoading || isBonusApplied}
                                            />
                                            <Button
                                                type="primary"
                                                className="promo-btn"
                                                onClick={handleApplyPromo}
                                                disabled={isPromoLoading || !promoCode.trim() || isBonusApplied}
                                                title={isBonusApplied ? 'Промокод нельзя использовать вместе с баллами' : ''}
                                            >
                                                {isPromoLoading ? <Spin indicator={<LoadingOutlined spin />} size="small" /> : 'Применить'}
                                            </Button>
                                        </>
                                    )}
                                </div>

                                <div className="summary-block">
                                    <div className="block-header">
                                        <h3>Получатель</h3>
                                        <EditOutlined
                                            className="edit-icon"
                                            onClick={handleOpenAddressModal}
                                        />
                                    </div>
                                    {getRecipientDisplay()}
                                </div>

                                <div className="summary-block">
                                    <div className="block-header">
                                        <h3>Адрес</h3>
                                        {!allVirtual && hasShippingOptions && (
                                            <ReloadOutlined
                                                className="edit-icon"
                                                onClick={handleRecalculateShipping}
                                                spin={isShippingLoading}
                                                title="Пересчитать доставку"
                                            />
                                        )}
                                    </div>
                                    <AddressInput
                                        placeholder="Введите адрес..."
                                        autoCalculate={!allVirtual}
                                        cartItems={selectedCartItems}
                                    />
                                    {shippingAddress?.fullAddress && (
                                        <div style={{ marginTop: 8 }}>
                                            {getAddressDisplay()}
                                        </div>
                                    )}
                                </div>

                                {!allVirtual && (
                                    <div className="summary-block">
                                        <div className="block-header">
                                            <h3>Способ доставки</h3>
                                        </div>
                                        <ShippingOptions />
                                    </div>
                                )}

                                {allVirtual && (
                                    <div className="summary-block">
                                        <div className="summary-row">
                                            <span>Доставка</span>
                                            <span>бесплатно</span>
                                        </div>
                                    </div>
                                )}

                                <div className="cart-divider"/>

                                <div className="total-pay-row">
                                    <span className="total-label">К оплате</span>
                                    <div className="total-right">
                                        <span className="total-value">{Math.round(totalPrice).toLocaleString()}₽</span>
                                        <span className="vat-text">(включая {Math.round(vatAmount).toLocaleString()}₽ НДС)</span>
                                    </div>
                                </div>

                                <div className="payment-methods">
                                    <button className="payment-row" onClick={() => setPaymentMethod(paymentMethod === 'tinkoff' ? null : 'tinkoff')}>
                                        <Checkbox className="custom-check" checked={paymentMethod === 'tinkoff'} />
                                        <span>Оплата онлайн</span>
                                    </button>
                                    <button className="payment-row" onClick={() => setPaymentMethod(paymentMethod === 'cheque' ? null : 'cheque')}>
                                        <Checkbox className="custom-check" checked={paymentMethod === 'cheque'} />
                                        <span>В кредит</span>
                                    </button>
                                    {isOwnPickup && (
                                        <button className="payment-row" onClick={() => setPaymentMethod(paymentMethod === 'cod' ? null : 'cod')}>
                                            <Checkbox className="custom-check" checked={paymentMethod === 'cod'} />
                                            <span>Оплата при получении</span>
                                        </button>
                                    )}
                                </div>

                                <div className="legal-text">
                                    Ваши личные данные будут использоваться для обработки ваших заказов, упрощения вашей работы с сайтом и для других целей, описанных в нашей <a href="#/privacy-policy">политика конфиденциальности</a>.
                                </div>

                                <div className="agree-row">
                                    <Checkbox
                                        className="custom-check"
                                        checked={isAgreed}
                                        onChange={(e) => setIsAgreed(e.target.checked)}
                                    />
                                    <span>Я прочитал(а) и принимаю <a href="#" onClick={(e) => { e.preventDefault(); setIsTermsOpen(true); }}>правила и условия</a> сайта *</span>
                                </div>

                                <ConfirmOrderButton
                                    addClassName='confirm-btn'
                                    buttonText='Подтвердить заказ'
                                    paymentMethod={paymentMethod}
                                    disabled={!isAgreed || isShippingLoading}
                                />

                            </div>
                        </div>
                    )}
                </div>

                <Modal
                    title="Данные получателя"
                    open={isAddressModalOpen}
                    onCancel={handleCloseAddressModal}
                    footer={[
                        <Button
                            key="submit"
                            type="primary"
                            onClick={handleCloseAddressModal}
                            className="modal-submit-btn"
                        >
                            Сохранить
                        </Button>
                    ]}
                    width={800}
                    className="address-modal"
                    centered
                >
                    <AddressFormCart userData={userData} />
                </Modal>

                <VariationModal
                    open={!!editingItem}
                    onClose={() => setEditingItem(null)}
                    product={editingItem}
                    mode="edit"
                />

                <Modal
                    title="Правила и условия"
                    open={isTermsOpen}
                    onCancel={() => setIsTermsOpen(false)}
                    footer={null}
                    width={700}
                    className="terms-modal"
                    centered
                >
                    <div className="terms-content">
                        <h3>Основные определения</h3>
                        <p><strong>Горячая линия</strong> — телефон службы поддержки Клиентов.</p>
                        <p><strong>Доставка</strong> — доставка Товара до места назначения, обозначенного Клиентом при оформлении Заказа на Сайте в случае, если Клиентом выбрана услуга «доставка».</p>
                        <p><strong>Доставщик</strong> — ИП, или физическое, или юридическое лицо, оказывающее услуги по Доставке Товара, с которым Клиент при оформлении Заказа заключает договор на Доставку; либо Партнёр e-Feed, осуществляющий Доставку Товара своими силами или с привлечением третьих лиц.</p>
                        <p><strong>Заказ</strong> — оформленный Клиентом на Сайте заказ на покупку Товара, в результате которого Клиент заключает договор на приобретение Товара с e-Feed или Партнёром e-Feed, договор на Сборку данного Товара со Сборщиком, а в случае осуществления Доставки также договор Доставки данного Товара с Доставщиком на условиях, указанных в Правилах.</p>
                        <p><strong>Заявление о возврате</strong> — заявление Клиента о возврате Товара надлежащего либо ненадлежащего качества с указанием ФИО, паспортных данных, адреса регистрации Клиента и реквизитов карты и/или банковского счёта для возврата денежных средств.</p>
                        <p><strong>Партнёр e-Feed</strong> — юридическое лицо или ИП, являющиеся продавцами Товаров, предлагаемых Клиентам посредством Сайта.</p>
                        <p><strong>Клиент</strong> — физическое лицо, обладающее дееспособностью и необходимыми полномочиями для приобретения и оплаты Товаров через Сайт и осуществляющее регистрацию и/или заказ на Сайте.</p>
                        <p><strong>Личный кабинет</strong> — учётная запись Клиента на Сайте, создаваемая в момент регистрации Клиента и содержащая данные Клиента, указанные при регистрации или последующем изменении сведений, и сведения о Заказах Клиента.</p>
                        <p><strong>Получатель</strong> — физическое лицо, указанное Клиентом при оформлении Заказа в качестве лица, которому Доставщик передает заказанные Товары, либо сам Клиент.</p>
                        <p><strong>Промокод</strong> — уникальная комбинация букв и/или цифр, при условии активации которой Клиенту предоставляется скидка на стоимость Заказа в размере и на условиях, указанных в правилах соответствующей акции.</p>
                        <p><strong>Самовывоз</strong> — действие Клиента, выражающееся в самостоятельном получении Товара непосредственно в выбранной Клиентом Торговой точке.</p>
                        <p><strong>Сборщик</strong> — ИП, или физическое, или юридическое лицо, оказывающее услуги по Сборке Товара, с которым Клиент при оформлении Заказа заключает договор на Сборку; либо Партнёр e-Feed, осуществляющий Сборку Товара своими силами или с привлечением третьих лиц; либо e-Feed, осуществляющий Сборку Товаров своими силами или с привлечением третьих лиц.</p>

                        <h3>Политика конфиденциальности</h3>
                        <p>«Мы» — первый в России аграрный маркетплейс e-Feed, и в данном документе мы расскажем, как и с какой целью мы, ООО «ПРОТЕКТФИД» («мы», «нами» или «e-Feed») собираем, храним, используем ваши персональные данные, делимся ими и иным образом обрабатываем их, когда вы посещаете или используете сайт e-feed.ru (далее — «Сайт»).</p>
                        <p>Мы обеспечиваем защиту персональных данных наших пользователей и гарантируем прозрачность в их обработке.</p>

                        <h3>Данные, получаемые в процессе использования сайта</h3>
                        <p>Во время использования сайта мы можем получать такие данные, как:</p>
                        <ul>
                            <li>Куки</li>
                            <li>Данные о составе Ваших заказов, статусе и процессе их выполнения</li>
                            <li>Данные об избранных товарах</li>
                            <li>Данные об использовании промокодов</li>
                            <li>Данные об устройстве (IP-адрес, данные об ОС, браузере, разрешении экрана)</li>
                        </ul>

                        <h3>С кем мы делимся вашими данными</h3>
                        <p>Сайт вправе передавать персональные данные пользователей третьим лицам в следующих случаях:</p>
                        <ul>
                            <li>Пользователь явно выразил свое согласие на такие действия</li>
                            <li>Передача необходима для исполнения договора, заключенного с пользователем</li>
                            <li>Передача предусмотрена действующим законодательством</li>
                        </ul>

                        <h3>Какие у вас права на ваши данные</h3>
                        <p>Вы можете запросить файл экспорта персональных данных, которые мы сохранили о вас. Вы также можете запросить удаление этих данных, это не включает данные, которые мы обязаны хранить в административных целях, по закону или целях безопасности.</p>

                        <h3>Меры защиты данных</h3>
                        <ul>
                            <li>Введена система разграничения доступа</li>
                            <li>Установлена защита от несанкционированного доступа к рабочим местам, сетям и базам данных</li>
                            <li>Защита от вредоносного программно-математического воздействия</li>
                            <li>Регулярное резервное копирование информации и баз данных</li>
                            <li>Передача информации по сетям общего пользования с использованием криптографической защиты</li>
                            <li>Система контроля за порядком обработки персональных данных и обеспечения их безопасности</li>
                        </ul>

                        <p style={{ marginTop: 20 }}>Полная версия: <a href="#/privacy-policy" onClick={() => setIsTermsOpen(false)}>Политика конфиденциальности</a></p>
                    </div>
                </Modal>
            </div>
        </ConfigProvider>
    );
};

export default Cart;